import React, { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { governanceMockSnapshot } from "@/data/governance-mock";
import {
  Meeting,
  Committee,
  CommitteeMember,
  GovernanceFramework,
  GovernanceFrameworkStatus,
  GovernanceSnapshot,
  TermsOfReference,
  VoteChoice,
  VotingDecision,
  VotingDecisionStatus,
} from "@/types/governance";

interface CreateFrameworkInput {
  name: string;
  description: string;
  chairpersonId: string | null;
  createdBy: string;
}

interface CreateCommitteeInput {
  frameworkId: string;
  name: string;
  description: string;
  level: number;
  parentId: string | null;
  ownerId: string | null;
  type: Committee["type"];
  memberIds?: string[];
}

interface CreateMeetingInput {
  frameworkId: string;
  committeeId: string;
  title: string;
  date: string;
  attendees: string[];
  agendaItems: string[];
  decisions: string[];
  actionItems: Meeting["actionItems"];
  referencedTorIds: string[];
}

interface CreateVotingDecisionInput {
  meetingId: string;
  committeeId: string;
  frameworkId: string;
  title: string;
  description: string;
  proposedBy: string;
  deadline?: string;
}

interface GovernanceContextValue extends GovernanceSnapshot {
  getActiveFramework: () => GovernanceFramework | undefined;
  getFrameworkById: (frameworkId: string) => GovernanceFramework | undefined;
  getCommitteesByFramework: (frameworkId: string) => Committee[];
  getTermsByFramework: (frameworkId: string) => TermsOfReference[];
  getCommitteeById: (committeeId: string) => Committee | undefined;
  getTorByCommitteeId: (committeeId: string) => TermsOfReference | undefined;
  getMemberById: (memberId: string | null | undefined) => CommitteeMember | undefined;
  getMeetingsByCommittee: (committeeId: string) => Meeting[];
  getMeetingById: (meetingId: string) => Meeting | undefined;
  getVotingDecisionsByMeeting: (meetingId: string) => VotingDecision[];
  getVotingDecisionsByFramework: (frameworkId: string) => VotingDecision[];
  addFramework: (input: CreateFrameworkInput) => GovernanceFramework;
  updateFramework: (frameworkId: string, patch: Partial<GovernanceFramework>) => void;
  updateFrameworkStatus: (frameworkId: string, status: GovernanceFrameworkStatus) => void;
  addCommittee: (input: CreateCommitteeInput) => Committee;
  updateCommittee: (committeeId: string, patch: Partial<Committee>) => void;
  updateTor: (torId: string, patch: Partial<TermsOfReference>) => void;
  saveTorDraft: (torId: string, patch: Partial<TermsOfReference>) => void;
  submitTorForApproval: (torId: string) => void;
  markTorApproved: (torId: string, userId: string, comment?: string) => void;
  addMeeting: (input: CreateMeetingInput) => Meeting;
  updateMeeting: (meetingId: string, patch: Partial<Meeting>) => void;
  publishMinutes: (meetingId: string) => void;
  addVotingDecision: (input: CreateVotingDecisionInput) => VotingDecision;
  castVote: (decisionId: string, memberId: string, vote: VoteChoice) => void;
  updateVotingDecisionStatus: (decisionId: string, status: VotingDecisionStatus) => void;
}

const GovernanceContext = createContext<GovernanceContextValue | undefined>(undefined);

const uid = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

export function GovernanceProvider({ children }: { children: ReactNode }) {
  const [members] = useState<CommitteeMember[]>(governanceMockSnapshot.members);
  const [frameworks, setFrameworks] = useState<GovernanceFramework[]>(governanceMockSnapshot.frameworks);
  const [committees, setCommittees] = useState<Committee[]>(governanceMockSnapshot.committees);
  const [terms, setTerms] = useState<TermsOfReference[]>(governanceMockSnapshot.terms);
  const [meetings, setMeetings] = useState<Meeting[]>(governanceMockSnapshot.meetings);
  const [votingDecisions, setVotingDecisions] = useState<VotingDecision[]>(
    governanceMockSnapshot.votingDecisions,
  );

  const getActiveFramework = () => frameworks[0];
  const getFrameworkById = (frameworkId: string) => frameworks.find((item) => item.id === frameworkId);
  const getCommitteesByFramework = (frameworkId: string) =>
    committees.filter((item) => item.frameworkId === frameworkId);
  const getTermsByFramework = (frameworkId: string) => terms.filter((item) => item.frameworkId === frameworkId);
  const getCommitteeById = (committeeId: string) => committees.find((item) => item.id === committeeId);
  const getTorByCommitteeId = (committeeId: string) => terms.find((item) => item.committeeId === committeeId);
  const getMemberById = (memberId: string | null | undefined) =>
    members.find((item) => item.id === memberId);
  const getMeetingsByCommittee = (committeeId: string) =>
    meetings.filter((item) => item.committeeId === committeeId);
  const getMeetingById = (meetingId: string) => meetings.find((item) => item.id === meetingId);
  const getVotingDecisionsByMeeting = (meetingId: string) =>
    votingDecisions.filter((item) => item.meetingId === meetingId);
  const getVotingDecisionsByFramework = (frameworkId: string) =>
    votingDecisions.filter((item) => item.frameworkId === frameworkId);

  const addFramework = (input: CreateFrameworkInput) => {
    const timestamp = new Date().toISOString();
    const framework: GovernanceFramework = {
      id: uid("f"),
      name: input.name,
      description: input.description,
      status: "draft",
      chairpersonId: input.chairpersonId,
      createdBy: input.createdBy,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    setFrameworks((prev) => [framework, ...prev]);
    return framework;
  };

  const updateFrameworkStatus = (frameworkId: string, status: GovernanceFrameworkStatus) => {
    setFrameworks((prev) =>
      prev.map((item) =>
        item.id === frameworkId
          ? {
              ...item,
              status,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  };

  const updateFramework = (frameworkId: string, patch: Partial<GovernanceFramework>) => {
    setFrameworks((prev) =>
      prev.map((item) =>
        item.id === frameworkId
          ? {
              ...item,
              ...patch,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  };

  const addCommittee = (input: CreateCommitteeInput) => {
    const committee: Committee = {
      id: uid("c"),
      frameworkId: input.frameworkId,
      name: input.name,
      description: input.description,
      level: input.level,
      parentId: input.parentId,
      ownerId: input.ownerId,
      type: input.type,
      chairpersonId: null,
      memberIds: input.memberIds ?? [],
      minimumMembersRequired: 3,
      meetingFrequency: "monthly",
      reportingTo: "",
      approvalAuthority: "",
      torId: null,
    };
    setCommittees((prev) => [...prev, committee]);
    return committee;
  };

  const updateCommittee = (committeeId: string, patch: Partial<Committee>) => {
    setCommittees((prev) =>
      prev.map((item) => (item.id === committeeId ? { ...item, ...patch } : item)),
    );
  };

  const updateTor = (torId: string, patch: Partial<TermsOfReference>) => {
    setTerms((prev) =>
      prev.map((item) =>
        item.id === torId
          ? {
              ...item,
              ...patch,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  };

  const saveTorDraft = (torId: string, patch: Partial<TermsOfReference>) => {
    updateTor(torId, { ...patch, status: "draft" });
  };

  const submitTorForApproval = (torId: string) => {
    updateTor(torId, { status: "submitted" });
  };

  const markTorApproved = (torId: string, userId: string, comment?: string) => {
    setTerms((prev) =>
      prev.map((item) => {
        if (item.id !== torId) return item;
        const approval = {
          id: uid("al"),
          torId,
          userId,
          action: "approved" as const,
          comment,
          createdAt: new Date().toISOString(),
        };
        const approvalLog = [...item.approvalLog, approval];
        const approvedBy = new Set(approvalLog.map((log) => log.userId));
        const isFullyApproved = item.approverIds.length > 0 && item.approverIds.every((id) => approvedBy.has(id));
        return {
          ...item,
          approvalLog,
          status: isFullyApproved ? "approved" : item.status,
          approvalDate: isFullyApproved ? new Date().toISOString() : item.approvalDate,
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  const addMeeting = (input: CreateMeetingInput) => {
    const meeting: Meeting = {
      id: uid("mtg"),
      frameworkId: input.frameworkId,
      committeeId: input.committeeId,
      title: input.title,
      date: input.date,
      attendees: input.attendees,
      agendaItems: input.agendaItems,
      decisions: input.decisions,
      actionItems: input.actionItems,
      referencedTorIds: input.referencedTorIds,
      status: "draft",
    };
    setMeetings((prev) => [meeting, ...prev]);
    return meeting;
  };

  const updateMeeting = (meetingId: string, patch: Partial<Meeting>) => {
    setMeetings((prev) =>
      prev.map((item) => (item.id === meetingId ? { ...item, ...patch } : item)),
    );
  };

  const publishMinutes = (meetingId: string) => {
    updateMeeting(meetingId, {
      status: "published",
      minutesSentAt: new Date().toISOString(),
    });
  };

  const addVotingDecision = (input: CreateVotingDecisionInput) => {
    const decision: VotingDecision = {
      id: uid("vote"),
      meetingId: input.meetingId,
      committeeId: input.committeeId,
      frameworkId: input.frameworkId,
      title: input.title,
      description: input.description,
      proposedBy: input.proposedBy,
      deadline: input.deadline,
      status: "open",
      votes: [],
    };
    setVotingDecisions((prev) => [decision, ...prev]);
    return decision;
  };

  const castVote = (decisionId: string, memberId: string, vote: VoteChoice) => {
    setVotingDecisions((prev) =>
      prev.map((decision) => {
        if (decision.id !== decisionId) return decision;
        const nextVote = { memberId, vote, castAt: new Date().toISOString() };
        const existingIndex = decision.votes.findIndex((item) => item.memberId === memberId);
        const votes =
          existingIndex >= 0
            ? decision.votes.map((item, index) => (index === existingIndex ? nextVote : item))
            : [...decision.votes, nextVote];
        return { ...decision, votes };
      }),
    );
  };

  const updateVotingDecisionStatus = (decisionId: string, status: VotingDecisionStatus) => {
    setVotingDecisions((prev) =>
      prev.map((item) => (item.id === decisionId ? { ...item, status } : item)),
    );
  };

  const value = useMemo<GovernanceContextValue>(
    () => ({
      members,
      frameworks,
      committees,
      terms,
      meetings,
      votingDecisions,
      getActiveFramework,
      getFrameworkById,
      getCommitteesByFramework,
      getTermsByFramework,
      getCommitteeById,
      getTorByCommitteeId,
      getMemberById,
      getMeetingsByCommittee,
      getMeetingById,
      getVotingDecisionsByMeeting,
      getVotingDecisionsByFramework,
      addFramework,
      updateFramework,
      updateFrameworkStatus,
      addCommittee,
      updateCommittee,
      updateTor,
      saveTorDraft,
      submitTorForApproval,
      markTorApproved,
      addMeeting,
      updateMeeting,
      publishMinutes,
      addVotingDecision,
      castVote,
      updateVotingDecisionStatus,
    }),
    [members, frameworks, committees, terms, meetings, votingDecisions],
  );

  return React.createElement(GovernanceContext.Provider, { value }, children);
}

export function useGovernance() {
  const context = useContext(GovernanceContext);
  if (!context) {
    throw new Error("useGovernance must be used within GovernanceProvider");
  }
  return context;
}
